import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './ReceptionistLogin.css';

function ReceptionistLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use Supabase Auth to sign in
      console.log('Attempting Supabase Auth sign in for receptionist:', formData.email);
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError) {
        console.error('Supabase Auth error:', authError);
        if (authError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else if (authError.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account before logging in.');
        } else {
          setError('Login failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      console.log('Supabase Auth sign in successful:', authData);

      // Get receptionist data from receptionists table using the auth user ID
      const { data: receptionistData, error: receptionistError } = await supabase
        .from('receptionists')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (receptionistError || !receptionistData) {
        console.error('Error fetching receptionist data:', receptionistError);
        setError('Receptionist profile not found. Please contact administrator.');
        setLoading(false);
        return;
      }

      // Check if receptionist is active
      if (!receptionistData.is_active) {
        setError('Your account has been deactivated. Please contact administrator.');
        setLoading(false);
        return;
      }

      // Store receptionist session
      localStorage.setItem('receptionistAuthenticated', 'true');
      localStorage.setItem('receptionistData', JSON.stringify(receptionistData));
      localStorage.setItem('supabaseAuthToken', authData.session.access_token);
      
      // Redirect to receptionist dashboard
      navigate('/admission');
      
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="receptionist-login-container">
      <div className="receptionist-login-card">
        <div className="login-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy Logo" 
            className="nexy-logo"
          />
          <h1>Receptionist Login</h1>
          <p>Welcome back! Please login to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message" role="alert" id="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              required
              disabled={loading}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? "error-message" : undefined}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                disabled={loading}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? "error-message" : undefined}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={0}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={(e) => setFormData(prev => ({ ...prev, rememberMe: e.target.checked }))}
                disabled={loading}
              />
              <span>Remember me</span>
            </label>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
            aria-label="Login to receptionist dashboard"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ReceptionistLogin;

